"use client";

import { Header } from "@/components/header";
import { useEffect, useRef, ReactNode } from "react";
import autofit from "autofit.js";

interface AutofitPageWrapperProps {
  children: ReactNode;
}

export const AutofitPageWrapper = ({ children }: AutofitPageWrapperProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // 确保容器可以正常接收滚轮事件
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    // 确保容器可以滚动
    const ensureScrollable = () => {
      if (container) {
        container.style.overflowY = "auto";
        container.style.overflowX = "hidden";
      }
    };
    
    // 定期检查并确保容器可滚动（防止 autofit 覆盖）
    const interval = setInterval(ensureScrollable, 500);
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    // 禁用 body 和 html 的滚动，避免双重滚动条
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    // 计算并设置滚动条宽度
    const updateScrollbarWidth = () => {
      if (containerRef.current) {
        const el = containerRef.current;
        const scrollbarWidth = el.offsetWidth - el.clientWidth;
        document.documentElement.style.setProperty(
          "--scrollbar-width",
          `${Math.max(0, scrollbarWidth)}px`
        );
      }
    };

    const initAutofit = () => {
      autofit.init({
        dh: window.innerHeight,
        dw: 1920,
        el: ".autofit-container",
        resize: true,
        allowScroll: true,
      });
      // autofit 初始化后更新滚动条宽度
      setTimeout(updateScrollbarWidth, 100);
      
      // 确保容器可以正常滚动 - 在 autofit 初始化后强制设置
      setTimeout(() => {
        if (containerRef.current) {
          const container = containerRef.current;
          // 确保容器可以接收滚轮事件和滚动
          container.style.overflowY = "auto";
          container.style.overflowX = "hidden";
          // 确保容器可以滚动（防止 autofit 覆盖样式）
          container.style.position = "relative";
        }
      }, 200);
    };

    // 初始化 autofit
    initAutofit();

    // 初始计算
    updateScrollbarWidth();

    // 使用 ResizeObserver 监听容器大小变化
    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current) {
      resizeObserver = new ResizeObserver(updateScrollbarWidth);
      resizeObserver.observe(containerRef.current);
    }

    // 监听窗口高度变化
    let lastHeight = window.innerHeight;
    const handleResize = () => {
      const currentHeight = window.innerHeight;
      // 只有当高度真正变化时才重新初始化
      if (Math.abs(currentHeight - lastHeight) > 1) {
        lastHeight = currentHeight;
        // 先关闭旧的 autofit
        autofit.off();
        // 重新初始化 autofit
        initAutofit();
      }
      // 更新滚动条宽度
      updateScrollbarWidth();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      // 清理时恢复滚动
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", handleResize);
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      autofit.off();
    };
  }, []);

  return (
    <>
      <Header />
      <div
        ref={containerRef}
        className="autofit-container"
        data-scroll-ref
        style={{
          width: "100%",
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          position: "relative",
        }}
      >
        {children}
      </div>
    </>
  );
};
